import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { resolveImg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ACCOUNT_STATUSES, ACCOUNT_STATUS_LABEL, PLANS, PLAN_LABEL, ACCESS_TYPES, ACCESS_TYPE_LABEL, statusBadge, fmtDate } from "@/lib/adminMeta";
import { ArrowLeft, Save, Building2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const normPlan = (p) => (["pro", "dealer_pro", "dealer_enterprise"].includes(p) ? "pro" : "core");

const Row = ({ label, value }) => (
  <div className="flex flex-col sm:flex-row sm:items-center gap-1 py-2 border-b border-border last:border-0">
    <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold sm:w-48 shrink-0">{label}</div>
    <div className="text-sm text-navy break-words">{value || "-"}</div>
  </div>
);

export default function AdminDealerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [form, setForm] = useState({ account_status: "active", plan: "core", access_type: "complimentary", access_start: "", access_expiration: "", access_note: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get(`/admin/dealers/${id}`).then(({ data }) => {
      setD(data);
      setForm({
        account_status: data.account_status || "active",
        plan: normPlan(data.plan || "core"),
        access_type: data.access_type || "complimentary",
        access_start: (data.access_start || "").slice(0, 10),
        access_expiration: (data.access_expiration || "").slice(0, 10),
        access_note: data.access_note || "",
      });
    }).catch(() => setD(false));
  }, [id]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/admin/dealers/${id}`, form);
      setD((s) => ({ ...s, ...form, entitlements: data.entitlements || s.entitlements }));
      toast.success("Dealer account updated");
    } catch { toast.error("Could not update"); } finally { setSaving(false); }
  };

  if (d === false) return <div className="text-muted-foreground">Dealership not found.</div>;
  if (!d) return <div className="text-muted-foreground">Loading...</div>;
  const dl = d.dealership || {};
  const primary = (d.users || []).find((u) => ["owner", "admin"].includes(u.role)) || (d.users || [])[0] || {};

  return (
    <div className="space-y-6 fade-up max-w-4xl" data-testid="admin-dealer-detail">
      <button onClick={() => navigate("/admin/dealers")} className="flex items-center gap-1.5 text-sm font-semibold text-muted-foreground hover:text-navy" data-testid="dealer-detail-back"><ArrowLeft className="w-4 h-4" /> Back to Dealer Accounts</button>

      <div className="flex items-center gap-4">
        {dl.logo ? (
          <div className="bg-white rounded-xl border border-border p-2 shadow-sm"><img src={resolveImg(dl.logo)} alt={dl.name} className="h-12 max-w-[160px] object-contain" data-testid="dealer-logo" /></div>
        ) : (
          <div className="w-14 h-14 rounded-xl bg-navy/5 flex items-center justify-center"><Building2 className="w-6 h-6 text-navy" /></div>
        )}
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-3xl font-bold text-navy">{dl.name || "Dealership"}</h1>
          <span className={`text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full border ${statusBadge(d.account_status)}`}>{ACCOUNT_STATUS_LABEL[d.account_status] || d.account_status}</span>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="bg-white rounded-2xl border border-border p-6">
          <h2 className="text-lg font-bold text-navy mb-3">Dealership Profile</h2>
          <Row label="Primary Contact" value={primary.name} />
          <Row label="Contact Email" value={dl.email || primary.email} />
          <Row label="Phone" value={dl.phone} />
          <Row label="Location" value={[dl.city, dl.state].filter(Boolean).join(", ")} />
          <Row label="Website" value={dl.website} />
          <Row label="Current Plan" value={PLAN_LABEL[d.plan] || d.plan} />
          <Row label="Active Inventory" value={`${d.active_inventory} of ${d.total_inventory} total`} />
          <Row label="Users" value={(d.users || []).length} />
          <Row label="Date Created" value={fmtDate(dl.created_at)} />
        </div>

        <div className="space-y-6">
          <div className="bg-white rounded-2xl border border-border p-6 h-fit">
            <h2 className="text-lg font-bold text-navy mb-4">Account Management</h2>
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label>Account Status</Label>
                <Select value={form.account_status} onValueChange={(v) => setForm((s) => ({ ...s, account_status: v }))}>
                  <SelectTrigger data-testid="dealer-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">{ACCOUNT_STATUSES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Plan</Label>
                <Select value={form.plan} onValueChange={(v) => setForm((s) => ({ ...s, plan: v }))}>
                  <SelectTrigger data-testid="dealer-plan-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">{PLANS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Access Type</Label>
                <Select value={form.access_type} onValueChange={(v) => setForm((s) => ({ ...s, access_type: v }))}>
                  <SelectTrigger data-testid="dealer-access-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">{ACCESS_TYPES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Access Start</Label>
                  <input type="date" value={form.access_start} onChange={(e) => setForm((s) => ({ ...s, access_start: e.target.value }))}
                    className="w-full h-10 rounded-md border border-border px-3 text-sm" data-testid="dealer-access-start" />
                </div>
                <div className="space-y-1.5">
                  <Label>Expiration (optional)</Label>
                  <input type="date" value={form.access_expiration} onChange={(e) => setForm((s) => ({ ...s, access_expiration: e.target.value }))}
                    className="w-full h-10 rounded-md border border-border px-3 text-sm" data-testid="dealer-access-expiration" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Internal Note / Reason</Label>
                <textarea value={form.access_note} onChange={(e) => setForm((s) => ({ ...s, access_note: e.target.value.slice(0, 500) }))} rows={2}
                  placeholder="e.g. Pilot dealer" className="w-full rounded-md border border-border px-3 py-2 text-sm" data-testid="dealer-access-note" />
              </div>
              <p className="text-xs text-muted-foreground">Billing is not active yet. Access is granted here manually. Core and Pro share the same RecJet Mind intelligence; Pro adds automation capabilities.</p>
              <Button onClick={save} disabled={saving} className="w-full bg-navy hover:bg-navy-light text-white font-bold" data-testid="dealer-save"><Save className="w-4 h-4 mr-1.5" /> {saving ? "Saving..." : "Save Changes"}</Button>
            </div>
          </div>

          {d.entitlements && (
            <div className="bg-white rounded-2xl border border-border p-6" data-testid="dealer-entitlements">
              <h2 className="text-lg font-bold text-navy mb-2 flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-gold" /> Effective Entitlements</h2>
              <div className="text-xs text-muted-foreground mb-3">
                Effective plan: <span className="font-semibold text-navy">{PLAN_LABEL[d.entitlements.effective_plan] || d.entitlements.effective_plan}</span>
                {" | "}Access: <span className="font-semibold text-navy">{ACCESS_TYPE_LABEL[d.entitlements.access_type] || d.entitlements.access_type}</span>
                {d.entitlements.expired && <span className="text-red-600 font-semibold"> (expired)</span>}
              </div>
              <div className="flex flex-wrap gap-1.5">
                {(d.entitlements.entitlements || []).map((e) => (
                  <span key={e} className="text-[11px] font-medium text-navy bg-secondary px-2 py-1 rounded-full">{e}</span>
                ))}
              </div>
            </div>
          )}

          <div className="bg-white rounded-2xl border border-border p-6">
            <h2 className="text-lg font-bold text-navy mb-3">Users ({(d.users || []).length})</h2>
            <div className="divide-y divide-border">
              {(d.users || []).map((u) => (
                <div key={u.user_id} className="flex items-center justify-between py-2.5" data-testid={`dealer-user-${u.user_id}`}>
                  <div className="min-w-0"><div className="font-medium text-navy truncate">{u.name || "-"}</div><div className="text-xs text-muted-foreground truncate">{u.email}</div></div>
                  <span className="text-[10px] font-bold uppercase tracking-wider text-gold bg-gold/10 px-2 py-1 rounded-full">{u.role}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
